import cloneDeep from 'lodash/cloneDeep';
import React, { useRef } from 'react';
import { IFramework, useMoveEventListener } from '@/framework';
import { ITouchKitEvent } from '@/utils/event-kit';
import { computerImageLayerRatio } from '@/utils/layer-kit';
import { flipPoint, rotatePoint, translateYReverse } from '@/utils/math-kit';
import { IImageLayerProps, ILayerProps, LayerType, MIN_IMG_SIZE } from '@/version-types';
import { getLayerMoveDistanceByMoveResizeType, isImageOutOfRangeLayer } from '../utils';

const LayerResizeChangeAction: React.FC<{
    stageScale: number;
    framework: IFramework;
}> = ({ stageScale, framework }) => {
    const currentLayerRef = useRef<ILayerProps | undefined>(undefined);
    const originLayerRef = useRef<ILayerProps | undefined>(undefined);

    useMoveEventListener((e: ITouchKitEvent) => {
        if (e.isStart) {
            const layer = framework.operate.getActivePickLayer();
            currentLayerRef.current = cloneDeep(layer as ILayerProps);
            originLayerRef.current = cloneDeep(layer as ILayerProps);
        }
        // 渲染层不存在，不可操作
        if (
            !currentLayerRef.current ||
            currentLayerRef.current?.layerType !== LayerType.IMAGE_LAYER ||
            !originLayerRef.current
        ) {
            return null;
        }
        const originLayer = cloneDeep(currentLayerRef.current);
        const target = e.target;
        const dataType = target.getAttribute('data-operate-type');

        if (!dataType?.includes('crop-layer-resize')) return;

        const { data, rotate } = currentLayerRef.current;
        const { imageTransform } = data as IImageLayerProps;
        const { flipH, flipV } = imageTransform;

        // 以缩放点的对向点为原点
        // 获取位移的x,y相对于坐标原点的位移距离和新的宽高
        // 将位移的距离转为图层坐标系
        // 计算offsetX offsetY 和宽高
        const movePosition = rotatePoint(
            translateYReverse({
                x: Math.round(e.deltaX / stageScale),
                y: Math.round(e.deltaY / stageScale),
            }),
            rotate,
        );
        const { newSizes, movePoints } = getLayerMoveDistanceByMoveResizeType(
            currentLayerRef.current,
            dataType,
            movePosition,
        );

        for (let i = newSizes.length - 1; i >= 0; i--) {
            const newSize = newSizes[i];
            const movePoint = movePoints[i];
            const newLayer = cloneDeep(currentLayerRef.current) as ILayerProps;
            const { imageTransform: newImageTransform } = newLayer.data as IImageLayerProps;
            const {
                offsetX: oldOffsetXRatio,
                offsetY: oldOffsetYRatio,
                widthRatio: oldWidthRatio,
                heightRatio: oldHeightRatio,
                scale: oldImageScale,
            } = newImageTransform;
            const newLayerWidth = newSize.width;
            const newLayerHeight = newSize.height;
            const oldImageWidth = newLayer.width * oldWidthRatio * oldImageScale;
            const oldImageHeight = newLayer.height * oldHeightRatio * oldImageScale;
            const { widthRatio, heightRatio, scale } = computerImageLayerRatio(
                newLayerWidth,
                newLayerHeight,
                oldImageWidth,
                oldImageHeight,
            );
            newImageTransform.widthRatio = widthRatio;
            newImageTransform.heightRatio = heightRatio;
            newImageTransform.scale = scale;
            const oldOffsetX = oldOffsetXRatio * oldImageWidth;
            const oldOffsetY = oldOffsetYRatio * oldImageHeight;
            const newOffsetX = (oldOffsetX - flipPoint(movePoint, flipH, flipV).x) / oldImageWidth;
            const newOffsetY = (oldOffsetY - flipPoint(movePoint, flipH, flipV).y) / oldImageHeight;

            newImageTransform.offsetX = newOffsetX;
            newImageTransform.offsetY = newOffsetY;
            newLayer.width = newLayerWidth;
            newLayer.height = newLayerHeight;
            const isOut = isImageOutOfRangeLayer(newLayer);
            console.log(
                'layer====',
                newLayer.width,
                oldOffsetX,
                oldOffsetX - flipPoint(movePoint, flipH, flipV).x,
                isOut,
            );
            if (!isOut && newLayerWidth > MIN_IMG_SIZE && newLayerHeight > MIN_IMG_SIZE) {
                Object.assign(imageTransform, (newLayer.data as IImageLayerProps).imageTransform);
                currentLayerRef.current.width = newLayerWidth;
                currentLayerRef.current.height = newLayerHeight;
                // 已中心点作为参考点来算新的left和top
                const moveDistance = translateYReverse(
                    rotatePoint(
                        translateYReverse({
                            x: movePoints[i].x,
                            y: movePoints[i].y,
                        }),
                        -rotate,
                    ),
                );
                console.log(
                    'in====',
                    originLayer.width / 2 + moveDistance.x - newLayerWidth / 2,
                    originLayer.height / 2 + moveDistance.y - newLayerHeight / 2,
                );
                currentLayerRef.current.left =
                    currentLayerRef.current.left + originLayer.width / 2 + moveDistance.x - newLayerWidth / 2;
                currentLayerRef.current.top =
                    currentLayerRef.current.top + originLayer.height / 2 + moveDistance.y - newLayerHeight / 2;
                break;
            }
        }

        // imageTransform.offsetX = floor(imageTransform.offsetX, 8);
        // imageTransform.offsetY = floor(imageTransform.offsetY, 8);
        const resultLayer = cloneDeep(currentLayerRef.current);
        console.log('resultLayer=====', resultLayer.width, resultLayer.height, resultLayer.left, resultLayer.top);
        // FIXME changeLayerStyle(originLayerRef.current, resultLayer);
        framework.stage.addOrUpdate(cloneDeep(resultLayer));
    });
    return null;
};

export default LayerResizeChangeAction;
